import * as React from 'react';
import styled from 'styled-components';
import { CommonPageWrapper, HeadingWrapper } from 'admin/components/wrappers';
import { BlurredLabel, PageMainLabel } from 'app/components/SHLabel';
import CustomBreadCrumb from 'app/components/CustomBreadCrumb';
import { AdminSearch } from 'app/components/AdminSearchField';
import AdminCustomButton from 'app/components/AdminCustomButton';
import DataTable from './components/DataTable';
import { useDebounce } from 'hooks/useDebounce';
import { AmountCard } from './components/AmountCard';
import PaidIcon from '../../assets/icon/green-tick.svg';
import AmountIcon from '../../assets/icon/blue-dollar.svg';
import UnpaidIcon from '../../assets/icon/red-warning.svg';
import useFinancialApi from 'admin/hooks/useFinancialApi';
import { CommonLoader } from 'app/components/CommonLoader';
import AdminDropDown from 'app/components/AdminDropDown';
import { GeneralLookup } from 'services/openapi';

const ButtonWrapper = styled.div`
  display: flex;
  gap: 10px;
  padding: 30px 0 0;
  .field-style {
    display: flex;
  }
  .k-dropdownlist {
    max-height: 31px;
  }
`;

export const breadCrumpitems = [
    {
        id: '',
        text: 'Showyeda admin',
        disabled: true,
    },
    {
        id: '/admin/financial-report',
        text: 'Financial Report',
    },
];
export function FinancialReport() {
    const [searchTerm, setSearchTerm] = React.useState('');
    const [paidFilter, setPaidFilter] = React.useState<string | undefined>(
        undefined,
    );
    const [type, setType] = React.useState<string | undefined>(undefined);
    const {
        fetchFinancialSummary,
        fetchFinancialReportList,
        financialReportList,
        financialSummaryData,
        loading,
        handleDownloadFinancial,
        downloadLoading,
    } = useFinancialApi();
    const [sortField, setSortField] = React.useState<string>('name');
    const [sortOrder, setSortOrder] = React.useState<number>(0);
    const debounceTeamSearch = useDebounce(searchTerm);
    const [pageSkip, setPageSkip] = React.useState<number | undefined>(0);
    const [pageSize, setPageSize] = React.useState<number>(50);
    const [pageSizeSelected, setPageSizeSelected] = React.useState<GeneralLookup>(
        {
            id: '50',
            name: '50',
        },
    );
    const pageSizeOptions: GeneralLookup[] = [
        { id: '25', name: '25' },
        { id: '50', name: '50' },
        { id: '100', name: '100' },
        { id: '200', name: '200' },
    ];

    const handlePageSizeChange = (data: GeneralLookup) => {
        const newPageSize = parseInt(data.id, 10);
        setPageSize(newPageSize);
        setPageSizeSelected(data);
        setPageSkip(0);
        fetchFinancialReportList(
            0,
            newPageSize,
            debounceTeamSearch,
            paidFilter,
            type,
        );
    };

    const handleFinancialDownload = async () => {
        try {
            const url: any = await handleDownloadFinancial(
                debounceTeamSearch,
                paidFilter,
                type,
            );

            // Extract filename from URL
            const filename = url.split('/').pop();

            fetch(url)
                .then(response => response.blob())
                .then(blob => {
                    const objectUrl = URL.createObjectURL(blob);

                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.download = filename!; // Use the extracted filename

                    // Append link to body (required for Firefox)
                    document.body.appendChild(link);

                    // Trigger download
                    link.click();

                    // Clean up
                    document.body.removeChild(link);
                    URL.revokeObjectURL(objectUrl);
                })
                .catch(error => {
                    console.error('Error downloading file:', error);
                });

            return url;
        } catch (error) {
            console.error('Error downloading team point report:', error);
            throw error;
        }
    };
    React.useEffect(() => {
        if (debounceTeamSearch.length > 0) {
            fetchFinancialReportList(
                0,
                pageSize,
                debounceTeamSearch,
                paidFilter,
                type,
            );
            if (pageSkip !== 0) {
                setPageSkip(0);
            }
        } else if (debounceTeamSearch.length === 0) {
            fetchFinancialReportList(
                pageSkip,
                pageSize,
                debounceTeamSearch,
                paidFilter,
                type,
            );
        } else {
            fetchFinancialReportList(
                pageSkip,
                pageSize,
                debounceTeamSearch,
                paidFilter,
                type,
            );
        }
    }, [debounceTeamSearch, pageSize]);
    React.useEffect(() => {
        fetchFinancialSummary();
    }, []);
    const ANALYTICS_DATA = [
        {
            value: `$${financialSummaryData?.totalAmount}`,
            label: 'Total Amount',
            iconBgColor: 'bg-blue-400 opacity-10',
            iconSvg: AmountIcon,
        },
        {
            value: `$${financialSummaryData?.totalPaid}`,
            label: 'Total Paid',
            iconBgColor: 'bg-green-300 opacity-20',
            iconSvg: PaidIcon,
        },
        {
            value: `$${financialSummaryData?.totalUnpaid}`,
            label: 'Total Unpaid',
            iconBgColor: 'bg-red-400 opacity-10',
            iconSvg: UnpaidIcon,
        },
    ];
    return (
        <StyledCommonPageWrapper>
            {downloadLoading && <CommonLoader loaderType="main" />}
            <HeadingWrapper>
                <PageMainLabel>Financial Report</PageMainLabel>
                <CustomBreadCrumb preData={breadCrumpitems} />
            </HeadingWrapper>
            <GridContainer>
                {ANALYTICS_DATA.map((item, index) => (
                    <AmountCard
                        key={index}
                        amount={item.value}
                        title={item.label}
                        iconSvg={item.iconSvg}
                        loading={loading}
                    />
                ))}
            </GridContainer>
            <HeadingWrapper>
                <ButtonWrapper>
                    <BlurredLabel style={{ whiteSpace: 'nowrap' }}>
                        Popular filter
                    </BlurredLabel>
                    <AdminCustomButton
                        buttonType="primary"
                        isActive={paidFilter === 'Paid'}
                        className={
                            paidFilter === 'Paid' ? 'active-inactive' : 'Inactive-button'
                        }
                        onClick={() => {
                            if (paidFilter === 'Paid') {
                                setPaidFilter(undefined);
                                fetchFinancialReportList(
                                    pageSkip,
                                    pageSize,
                                    debounceTeamSearch,
                                    undefined,
                                    type,
                                );
                            } else {
                                setPaidFilter('Paid');
                                fetchFinancialReportList(
                                    pageSkip,
                                    pageSize,
                                    debounceTeamSearch,
                                    'Paid',
                                    type,
                                );
                            }
                        }}
                    >
                        Paid
                    </AdminCustomButton>
                    <AdminCustomButton
                        buttonType="primary"
                        isActive={paidFilter === 'Unpaid'}
                        className={
                            paidFilter === 'Unpaid' ? 'active-inactive' : 'Inactive-button'
                        }
                        onClick={() => {
                            if (paidFilter === 'Unpaid') {
                                setPaidFilter(undefined);
                                fetchFinancialReportList(
                                    pageSkip,
                                    pageSize,
                                    debounceTeamSearch,
                                    undefined,
                                    type,
                                );
                            } else {
                                setPaidFilter('Unpaid');
                                fetchFinancialReportList(
                                    pageSkip,
                                    pageSize,
                                    debounceTeamSearch,
                                    'Unpaid',
                                    type,
                                );
                            }
                        }}
                    >
                        Unpaid
                    </AdminCustomButton>
                </ButtonWrapper>
                <div className="search-wrapper">
                    <AdminCustomButton
                        buttonType="primary"
                        isActive={false}
                        className="fill-button"
                        onClick={handleFinancialDownload}
                        disabled={financialReportList?.items?.length === 0}
                    >
                        Download
                    </AdminCustomButton>
                    <div style={{ width: '283px', paddingTop: '30px' }}>
                        <AdminSearch
                            placeholder="Search by name"
                            onSearchTextEntering={e => {
                                setSearchTerm(e.target.value);
                            }}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                        />
                    </div>
                    {/* Currently not required
                    <AdminCustomButton
                        buttonType="primary"
                        isActive={false}
                        className="fill-button"
                        onClick={() => { }}
                    >
                        Download Excel
                    </AdminCustomButton> */}
                </div>
            </HeadingWrapper>
            {(financialReportList?.pageDetails?.count ?? 0) > 10 && (
                <EntriesPerPageDropDownWrapper>
                    <AdminDropDown
                        label="No: of entries per page"
                        onDataChange={(data: GeneralLookup) => {
                            handlePageSizeChange(data);
                        }}
                        defaultValue={pageSizeSelected}
                        data={pageSizeOptions}
                    />
                </EntriesPerPageDropDownWrapper>
            )}
            <DataTable
                financialReportList={financialReportList}
                setPageSkip={setPageSkip}
                pageSkip={pageSkip}
                fetchFinancialReportList={fetchFinancialReportList}
                debounceTeamSearch={debounceTeamSearch}
                sortField={sortField}
                sortOrder={sortOrder}
                setSortField={setSortField}
                setSortOrder={setSortOrder}
                loading={loading}
                paidFilter={paidFilter}
                setType={setType}
                type={type}
                pageSize={pageSize}
            />
        </StyledCommonPageWrapper>
    );
}
const StyledCommonPageWrapper = styled(CommonPageWrapper)`
  .fill-button {
    margin-right: 0px !important;
  }
`;
const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 42px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;
const EntriesPerPageDropDownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 10px;
  .SHdropdown {
    width: 160px;
    .label {
      margin-bottom: 8px;
      font-size: 13px;
      color: #475467;
      font-weight: 500;
    }
    .k-dropdownlist {
      max-height: 38px;
      border-radius: 8px !important;
    }
    .k-input-inner {
      padding: 0 8px !important;
      font-size: 13px;
    }
    .k-input-value-text {
      margin: 4px 0;
      font-size: 13px !important;
      color: #1e1e1e;
    }
  }
`;
